/**
 * P14's bulk-invite endpoint (`POST /gates/{id}/members/bulk`, D28/D31/D97), wrapped the same way
 * `../shared/api.ts`'s `inviteMember()` is. Lives beside `InviteScreen.tsx` rather than in
 * `screens/shared/api.ts` — that file is P7's, and this is P15's addition to `invite` (P15 §8
 * rule 1: additive, never a rewrite of a P7 file).
 *
 * **Every row now carries its own role.** §6's "assign as residents" described the fast-follow's
 * first cut, when the multi-select step was its own screen with no per-row field left to decide;
 * now that `InviteScreen.tsx` merges the single and multi-contact paths into one growing list,
 * every entry — however it was added — gets the same resident/manager choice the single-invite
 * flow always offered, defaulting to resident. `packages/functions/members/bulk.ts` already
 * accepted a role per row (`BulkRowInput.role`); only this client wrapper hard-coded it. Logged
 * as a deviation in `implementation-notes.md` → P15.
 *
 * **And its transport restriction (§5.2, P16), by the identical route.** `bulk.ts` has accepted
 * `BulkRowInput.transportRestriction` since P14 and only this wrapper omitted it — the same
 * sentence the paragraph above makes about `role`, one field over. It is optional because
 * **absence is the answer** (D46): a grant that restricts no transport carries no such attribute,
 * so the screen's `'any'` becomes nothing on the wire rather than a third enum value the server
 * would have to learn.
 */

import { buildPath, ROUTES, type E164, type MemberRole } from '@selogate/core'
import type { ApiRequest, ApiResponse } from '../../auth/transport'
import { field, numberField, stringField } from '../../auth/transport'
import type { Locale } from '../../i18n/locales'

export type ApiRequestFn = (request: ApiRequest) => Promise<ApiResponse>

export type BulkApiResult<T> =
  | { readonly ok: true; readonly status: number; readonly data: T }
  | { readonly ok: false; readonly status: number; readonly code?: string; readonly field?: string }

function ok<T>(status: number, data: T): BulkApiResult<T> {
  return { ok: true, status, data }
}

function fail<T>(response: ApiResponse): BulkApiResult<T> {
  const code = stringField(response.body, 'code')
  const fieldName = stringField(response.body, 'field')
  return {
    ok: false,
    status: response.status,
    ...(code === undefined ? {} : { code }),
    ...(fieldName === undefined ? {} : { field: fieldName }),
  }
}

function isSuccess(status: number): boolean {
  return status >= 200 && status < 300
}

/** D79 — which control produced a row. Declared locally: `packages/functions/*` is server-only. */
export type InviteSource = 'picker' | 'manual'

export interface BulkMemberInput {
  readonly phone: string
  readonly source: InviteSource
  readonly role: MemberRole
  /** §5.2/P16. Omit for a grant that restricts no transport — see the note above. */
  readonly transportRestriction?: 'cloud' | 'ble'
}

export type BulkRowOutcome = 'registered' | 'added' | 'invalid' | 'skipped-duplicate'

export interface BulkRow {
  readonly index: number
  readonly outcome: BulkRowOutcome
  readonly phone?: E164
  readonly role?: MemberRole
  readonly status?: 'active' | 'pending'
  readonly userId?: string
  /** A rule name on an `invalid` row — never the submitted value (D57). */
  readonly reason?: string
}

export interface BulkInviteResult {
  readonly rows: readonly BulkRow[]
  readonly summary: {
    readonly registered: number
    readonly added: number
    readonly invalid: number
    readonly skippedDuplicate: number
  }
  /** One, for the whole batch (D38) — never one per row. */
  readonly share: { readonly text: string; readonly url: string }
}

const BULK_OUTCOMES = new Set(['registered', 'added', 'invalid', 'skipped-duplicate'])

function readBulkRow(raw: unknown): BulkRow | undefined {
  const index = numberField(raw, 'index')
  const outcome = field(raw, 'outcome')
  if (index === undefined || typeof outcome !== 'string' || !BULK_OUTCOMES.has(outcome)) return undefined
  const phone = stringField(raw, 'phone')
  const role = field(raw, 'role')
  const status = field(raw, 'status')
  const userId = stringField(raw, 'userId')
  const reason = stringField(raw, 'reason')
  return {
    index,
    outcome: outcome as BulkRowOutcome,
    ...(phone === undefined ? {} : { phone: phone as E164 }),
    ...(role === 'manager' || role === 'resident' ? { role } : {}),
    ...(status === 'active' || status === 'pending' ? { status } : {}),
    ...(userId === undefined ? {} : { userId }),
    ...(reason === undefined ? {} : { reason }),
  }
}

function readBulkInviteResult(body: unknown): BulkInviteResult | undefined {
  const rowsRaw = field(body, 'rows')
  const share = field(body, 'share')
  const text = stringField(share, 'text')
  const url = stringField(share, 'url')
  if (!Array.isArray(rowsRaw) || text === undefined || url === undefined) return undefined
  const rows = rowsRaw.map(readBulkRow).filter((row): row is BulkRow => row !== undefined)
  const summary = field(body, 'summary')
  return {
    rows,
    summary: {
      registered: numberField(summary, 'registered') ?? 0,
      added: numberField(summary, 'added') ?? 0,
      invalid: numberField(summary, 'invalid') ?? 0,
      skippedDuplicate: numberField(summary, 'skippedDuplicate') ?? 0,
    },
    share: { text, url },
  }
}

export async function bulkInviteMembers(
  request: ApiRequestFn,
  gateId: string,
  members: readonly BulkMemberInput[],
  locale: Locale,
): Promise<BulkApiResult<BulkInviteResult>> {
  const response = await request({
    method: ROUTES.memberBulkInvite.method,
    path: buildPath(ROUTES.memberBulkInvite, { gateId }),
    body: {
      members: members.map((member) => ({
        phone: member.phone,
        role: member.role,
        source: member.source,
        // D46 — absent, never `null` and never an `'any'` the schema would refuse as an unknown
        // enum value. `BULK_ROW_FIELDS` accepts the key; it does not accept a third state.
        ...(member.transportRestriction === undefined
          ? {}
          : { transportRestriction: member.transportRestriction }),
      })),
      locale,
    },
  })
  if (!isSuccess(response.status)) return fail(response)
  const data = readBulkInviteResult(response.body)
  return data === undefined ? fail(response) : ok(response.status, data)
}
